import React, { useEffect, useState } from 'react';

import {
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO } from 'api/controllers/reservations';
import { NothingFoundMessage, SearchInput } from 'components/shared';
import { pagingOptions } from 'constants/arrays';

const EMPTY_TEXT_MAX_LENGTH = 100;

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
    },
    container: {
        flex: 1,
        minHeight: 0,
    },
    table: {
        '&& th:last-child': {
            padding: '0 10px 16px',
        },
    },
    row: {
        cursor: 'pointer',
    },
    cell: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 180,
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 40,
        minWidth: 0,
        '& .search-input': {
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
        },
        '&& .search-input .MuiFormControl-root': {
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0,
            height: 40,
        },
        '& .search-input .MuiFormLabel-root': {
            top: '50%',
            transform: 'translateY(-50%)',
            lineHeight: '20px',
        },
        '& .search-input .MuiInputAdornment-root.MuiInputAdornment-positionStart': {
            position: 'static',
            height: 0,
            maxHeight: 'none',
            margin: 0,
        },
        '&& .search-input .MuiIconButton-root': {
            position: 'absolute',
            top: '50%',
            right: 0,
            transform: 'translateY(-50%)',
            padding: 6,
            zIndex: 1,
        },
        '& .search-input .MuiInputBase-root': {
            alignItems: 'center',
            height: 40,
            position: 'static',
            marginTop: '0 !important',
        },
    },
    headerSort: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 20px',
        height: 40,
        padding: 0,
        marginRight: 4,
        lineHeight: 1,
        verticalAlign: 'middle',
        '& .MuiTableSortLabel-icon': {
            display: 'block',
            margin: 0,
            flexShrink: 0,
        },
    },
    empty: {
        height: 400,
        borderBottom: 'none',
    },
    loader: {
        margin: 15,
    },
    paginationToolbar: {
        '&&': {
            justifyContent: 'flex-end',
        },
    },
});

export interface ReservationColumn {
    id: string;
    label: string;
    getText: (reservation: ReservationDTO) => string;
    getSortValue?: (reservation: ReservationDTO) => string | number;
    render?: (reservation: ReservationDTO) => React.ReactNode;
}

interface ReservationsTableProps {
    columns: ReservationColumn[];
    reservations: ReservationDTO[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    emptyText?: string;
    sortLast?: (reservation: ReservationDTO) => boolean;
    /** Extra trailing cell for a row (e.g. an actions menu); the header cell is empty. */
    renderRowActions?: (reservation: ReservationDTO) => React.ReactNode;
    isLoading?: boolean;
    /** Returns the table to the first page when it changes; rows per page are kept. */
    pageResetKey?: string;
}

export default function ReservationsTable({
    columns,
    reservations,
    selectedId,
    onSelect,
    emptyText,
    sortLast,
    renderRowActions,
    isLoading,
    pageResetKey,
}: ReservationsTableProps) {
    const classes = useStyles();
    const [orderBy, setOrderBy] = useState(columns[0].id);
    const [isAscending, setIsAscending] = useState(true);
    const [isSortedByUser, setIsSortedByUser] = useState(false);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);

    useEffect(() => {
        setPage(0);
    }, [pageResetKey]);

    const setFilter = (columnId: string, value: string) => {
        setFilters({ ...filters, [columnId]: value });
        setPage(0);
    };

    const handleSort = (columnId: string) => {
        setIsSortedByUser(true);
        setIsAscending(orderBy === columnId ? !isAscending : true);
        setOrderBy(columnId);
    };

    const sortColumn = columns.find((column) => column.id === orderBy) ?? columns[0];
    const sortValue = sortColumn.getSortValue ?? sortColumn.getText;
    const rows = reservations
        .filter((reservation) =>
            columns.every((column) =>
                column
                    .getText(reservation)
                    .toLowerCase()
                    .includes((filters[column.id] ?? '').toLowerCase())
            )
        )
        .sort((a, b) => {
            const aIsLast = sortLast?.(a) ?? false;
            const bIsLast = sortLast?.(b) ?? false;
            if (aIsLast !== bIsLast) {
                return aIsLast ? 1 : -1;
            }

            const left = sortValue(a);
            const right = sortValue(b);
            const order =
                typeof left === 'number' && typeof right === 'number'
                    ? left - right
                    : String(left).localeCompare(String(right));
            // By default the trailing group runs from the most recent to the oldest.
            const isDescending = aIsLast && !isSortedByUser ? true : !isAscending;
            return isDescending ? -order : order;
        });
    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);

    return (
        <div className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader className={classes.table}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={isAscending ? 'asc' : 'desc'}
                                >
                                    <div className={classes.headerContent}>
                                        <TableSortLabel
                                            className={classes.headerSort}
                                            active={orderBy === column.id}
                                            direction={isAscending ? 'asc' : 'desc'}
                                            onClick={() => handleSort(column.id)}
                                            aria-label={`Sort by ${column.label}`}
                                        />
                                        <SearchInput
                                            style={{
                                                minHeight: 0,
                                                height: 40,
                                                padding: 0,
                                                width: '100%',
                                            }}
                                            value={filters[column.id] ?? ''}
                                            name={column.label}
                                            trimValue={false}
                                            onChange={(value) => setFilter(column.id, value)}
                                            onEnter={() => undefined}
                                            onReset={() => setFilter(column.id, '')}
                                        />
                                    </div>
                                </TableCell>
                            ))}
                            {renderRowActions && <TableCell />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <CircularProgress
                                        size={48}
                                        className={classes.loader}
                                        aria-label="loading"
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} className={classes.empty}>
                                    <NothingFoundMessage
                                        text={emptyText}
                                        maxLength={EMPTY_TEXT_MAX_LENGTH}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                        {!isLoading &&
                            rows
                                .slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage)
                                .map((reservation) => (
                                    <TableRow
                                        key={reservation.id}
                                        hover
                                        className={classes.row}
                                        selected={reservation.id === selectedId}
                                        onClick={() => onSelect(reservation.id)}
                                    >
                                        {columns.map((column) => (
                                            <TableCell key={column.id} className={classes.cell}>
                                                {column.render
                                                    ? column.render(reservation)
                                                    : column.getText(reservation)}
                                            </TableCell>
                                        ))}
                                        {renderRowActions && (
                                            <TableCell padding="checkbox">
                                                {renderRowActions(reservation)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={pagingOptions}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage={i18n.t('common.rowsPerPage')}
                page={currentPage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                classes={{ toolbar: classes.paginationToolbar }}
            />
        </div>
    );
}
